/**
 * iter-82 — Preview Font Warmup.
 *
 * Ensures the eight curated Design Control fonts (Merriweather, Playfair
 * Display, Lora, Inter, Poppins, plus a few weights) are loaded into
 * the same document as the Live Preview so the WYSIWYG matches the
 * exported PDF pixel-for-pixel.
 *
 * Idempotent: only injects the <link> once per page load, regardless
 * of how many previews mount.
 */
import { useEffect } from "react";

const LINK_ID = "bf360-design-fonts";

// Single Google Fonts request bundles every family we support. Cached
// forever by the browser + Google's edge, so the cold-load hit is
// tiny (~30-40 KB across all subsets).
const FONTS_HREF =
  "https://fonts.googleapis.com/css2" +
  "?family=Merriweather:wght@400;700&display=swap" +
  "&family=Playfair+Display:wght@400;600;700&display=swap" +
  "&family=Lora:wght@400;600&display=swap" +
  "&family=Inter:wght@400;500;600&display=swap" +
  "&family=Poppins:wght@400;500;600&display=swap";

const PRECONNECT_HREFS = [
  "https://fonts.googleapis.com",
  "https://fonts.gstatic.com",
];

export function useDesignFontWarmup() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (document.getElementById(LINK_ID)) return undefined;

    // Preconnect to the CDN so the CSS request can start before the
    // WOFF files themselves. Costs one round-trip we'd otherwise pay
    // three times.
    const injected = [];
    for (const href of PRECONNECT_HREFS) {
      const pre = document.createElement("link");
      pre.rel = "preconnect";
      pre.href = href;
      if (href.includes("gstatic")) pre.crossOrigin = "anonymous";
      document.head.appendChild(pre);
      injected.push(pre);
    }
    // The main stylesheet — has all faces.
    const link = document.createElement("link");
    link.id = LINK_ID;
    link.rel = "stylesheet";
    link.href = FONTS_HREF;
    document.head.appendChild(link);
    injected.push(link);

    // No cleanup: leave the fonts loaded so unmounting the preview
    // doesn't blow the cache away. Component-scoped cleanup would
    // cause a flicker every time the panel toggles.
    return () => {};
  }, []);
}

export default useDesignFontWarmup;
